/**
 * Sjednocené form komponenty pro cockpit sheety — Input, Textarea, Select,
 * Button, Section, Row, ActionHeader. Všechny berou tokeny z sheetStyle.js,
 * takže napříč MojeSmena/Flotila modaly vypadá vše stejně.
 *
 * Designové principy:
 *   - touch-friendly: input 44px, button 48px primární CTA
 *   - sjednocené barvy: text přes getTextColors(isDark)
 *   - focus state s accent ringem (box-shadow), žádné default modré
 *   - radius z tokenů (input 10, button 12, chip 999)
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { SHEET_TOKENS, getTextColors, getInnerCardSurface } from './sheetStyle';

// ───────────────────────────────────────────────────────────────────────
// Input — textový/numerický input v sheetu
// ───────────────────────────────────────────────────────────────────────
export function SheetInput({
    label, hint, error, isDark,
    accent = '#FF8A00',
    rightSlot = null,
    style = {},
    ...rest
}) {
    const [focused, setFocused] = useState(false);
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);

    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.controlGap, minWidth: 0 }}>
            {label && (
                <span style={{
                    fontSize: SHEET_TOKENS.typography.label.size,
                    fontWeight: SHEET_TOKENS.typography.label.weight,
                    letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                    textTransform: SHEET_TOKENS.typography.label.textTransform,
                    color: colors.labelMuted,
                }}>{label}</span>
            )}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                height: SHEET_TOKENS.sizes.inputHeight,
                padding: '0 12px',
                background: inner.background,
                border: `1px solid ${focused ? accent : (error ? colors.danger : inner.border.split(' ').pop())}`,
                borderRadius: SHEET_TOKENS.radius.input,
                boxShadow: focused ? `0 0 0 3px ${accent}1F` : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
            }}>
                <input
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    {...rest}
                    style={{
                        flex: 1, minWidth: 0,
                        background: 'transparent', border: 'none', outline: 'none',
                        color: colors.textPrimary,
                        fontSize: SHEET_TOKENS.typography.body.size,
                        fontWeight: SHEET_TOKENS.typography.body.weight,
                        padding: 0,
                        ...style,
                    }}
                />
                {rightSlot}
            </div>
            {hint && !error && (
                <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.textMuted }}>{hint}</span>
            )}
            {error && (
                <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.danger }}>{error}</span>
            )}
        </label>
    );
}

// ───────────────────────────────────────────────────────────────────────
// Textarea — multiline input
// ───────────────────────────────────────────────────────────────────────
export function SheetTextarea({
    label, hint, error, isDark,
    accent = '#FF8A00',
    rows = 4,
    style = {},
    ...rest
}) {
    const [focused, setFocused] = useState(false);
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);

    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.controlGap, minWidth: 0 }}>
            {label && (
                <span style={{
                    fontSize: SHEET_TOKENS.typography.label.size,
                    fontWeight: SHEET_TOKENS.typography.label.weight,
                    letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                    textTransform: SHEET_TOKENS.typography.label.textTransform,
                    color: colors.labelMuted,
                }}>{label}</span>
            )}
            <textarea
                rows={rows}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                {...rest}
                style={{
                    width: '100%', minWidth: 0,
                    padding: '12px 14px',
                    background: inner.background,
                    border: `1px solid ${focused ? accent : (error ? colors.danger : inner.border.split(' ').pop())}`,
                    borderRadius: SHEET_TOKENS.radius.input,
                    boxShadow: focused ? `0 0 0 3px ${accent}1F` : 'none',
                    color: colors.textPrimary,
                    fontSize: SHEET_TOKENS.typography.body.size,
                    fontWeight: SHEET_TOKENS.typography.body.weight,
                    fontFamily: 'inherit',
                    outline: 'none', resize: 'vertical',
                    lineHeight: 1.4,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    ...style,
                }}
            />
            {hint && !error && (
                <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.textMuted }}>{hint}</span>
            )}
            {error && (
                <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.danger }}>{error}</span>
            )}
        </label>
    );
}

// ───────────────────────────────────────────────────────────────────────
// Select — vybírání z option
// ───────────────────────────────────────────────────────────────────────
export function SheetSelect({
    label, hint, error, isDark,
    accent = '#FF8A00',
    options = [], placeholder = '— vyber —',
    value, onChange,
    style = {},
    ...rest
}) {
    const [focused, setFocused] = useState(false);
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);

    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.controlGap, minWidth: 0 }}>
            {label && (
                <span style={{
                    fontSize: SHEET_TOKENS.typography.label.size,
                    fontWeight: SHEET_TOKENS.typography.label.weight,
                    letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                    textTransform: SHEET_TOKENS.typography.label.textTransform,
                    color: colors.labelMuted,
                }}>{label}</span>
            )}
            <select
                value={value ?? ''}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                {...rest}
                style={{
                    height: SHEET_TOKENS.sizes.inputHeight,
                    padding: '0 12px',
                    background: inner.background,
                    border: `1px solid ${focused ? accent : (error ? colors.danger : inner.border.split(' ').pop())}`,
                    borderRadius: SHEET_TOKENS.radius.input,
                    boxShadow: focused ? `0 0 0 3px ${accent}1F` : 'none',
                    color: colors.textPrimary,
                    fontSize: SHEET_TOKENS.typography.body.size,
                    fontWeight: SHEET_TOKENS.typography.body.weight,
                    outline: 'none',
                    appearance: 'menulist',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    ...style,
                }}>
                {placeholder && <option value="" disabled>{placeholder}</option>}
                {options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            {hint && !error && (
                <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.textMuted }}>{hint}</span>
            )}
            {error && (
                <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.danger }}>{error}</span>
            )}
        </label>
    );
}

// ───────────────────────────────────────────────────────────────────────
// Button — primary (gradient), secondary (solid muted), ghost (text only)
// ───────────────────────────────────────────────────────────────────────
export function SheetButton({
    variant = 'primary',          // 'primary' | 'secondary' | 'ghost' | 'danger'
    accent = '#FF8A00',
    accent2 = '#F59E0B',           // primary gradient end
    icon = null,
    busy = false,
    busyLabel = 'Pracuji…',
    disabled = false,
    isDark,
    fullWidth = true,
    children, onClick, type = 'button',
    style = {},
    ...rest
}) {
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);

    const base = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        width: fullWidth ? '100%' : 'auto',
        height: SHEET_TOKENS.sizes.buttonHeight,
        padding: '0 18px',
        borderRadius: SHEET_TOKENS.radius.button,
        fontSize: 15, fontWeight: 700, letterSpacing: 0.1,
        cursor: (busy || disabled) ? 'not-allowed' : 'pointer',
        opacity: (busy || disabled) ? 0.55 : 1,
        transition: 'transform 0.1s, box-shadow 0.15s, background 0.15s',
        outline: 'none',
        ...style,
    };

    let variantStyle = {};
    if (variant === 'primary') {
        variantStyle = {
            background: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
            color: isDark ? '#1a1a1f' : '#fff',
            border: 'none',
            boxShadow: (busy || disabled) ? 'none' : `0 6px 20px ${accent}59`,
        };
    } else if (variant === 'secondary') {
        variantStyle = {
            background: inner.background,
            color: colors.textPrimary,
            border: inner.border,
        };
    } else if (variant === 'danger') {
        variantStyle = {
            background: 'rgba(239,68,68,0.12)',
            color: colors.danger,
            border: '1px solid rgba(239,68,68,0.30)',
        };
    } else { // ghost
        variantStyle = {
            background: 'transparent',
            color: colors.textMuted,
            border: 'none',
        };
    }

    return (
        <button
            type={type}
            disabled={busy || disabled}
            onClick={onClick}
            {...rest}
            style={{ ...base, ...variantStyle }}
        >
            {icon}
            <span>{busy ? busyLabel : children}</span>
        </button>
    );
}

// ───────────────────────────────────────────────────────────────────────
// ActionHeader — header pro action sheety (cockpit signature look)
// Dot v accent + uppercase label, vpravo X close. Krátký řádek bez divideru
// uvnitř sheetu, typicky následuje hlavní obsah ihned.
// ───────────────────────────────────────────────────────────────────────
export function SheetActionHeader({ accent = '#FF8A00', label, onClose, isDark, style = {} }) {
    const colors = getTextColors(isDark);
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 8, ...style,
        }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                color: accent,
                fontSize: SHEET_TOKENS.typography.actionLabel.size,
                fontWeight: SHEET_TOKENS.typography.actionLabel.weight,
                letterSpacing: SHEET_TOKENS.typography.actionLabel.letterSpacing,
                textTransform: SHEET_TOKENS.typography.actionLabel.textTransform,
            }}>
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: accent, boxShadow: `0 0 8px ${accent}80`,
                }} />
                {label}
            </span>
            {onClose && (
                <button type="button" onClick={onClose} aria-label="Zavřít" style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: colors.textMuted, padding: 4, lineHeight: 0,
                }}>
                    <X size={20} />
                </button>
            )}
        </div>
    );
}

// ───────────────────────────────────────────────────────────────────────
// TitleHeader — header pro info sheety s názvem (PhaseModal, TankovaniModal…)
// Stejný jako standardní modál mobilní aplikace (components/SheetModal):
// ikona v barevné dlaždici + titulek + volitelný subtitle + X close.
// Bottom border divider — sjednocený padding 14px 16px.
// ───────────────────────────────────────────────────────────────────────
export function SheetTitleHeader({
    icon = null,
    iconColor = '#FF8A00',
    title,
    subtitle = null,
    onClose,
    isDark,
    withDivider = true,
    style = {},
}) {
    const colors = getTextColors(isDark);
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, padding: `${SHEET_TOKENS.spacing.headerPadY}px ${SHEET_TOKENS.spacing.sheetPadX}px`,
            borderBottom: withDivider ? `1px solid ${colors.divider}` : 'none',
            flexShrink: 0,
            ...style,
        }}>
            {icon && (
                <span style={{
                    width: 32, height: 32, borderRadius: 7, flexShrink: 0,
                    background: `${iconColor}2E`, color: iconColor,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>{icon}</span>
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{
                    color: colors.textPrimary, fontSize: 15, fontWeight: 700, lineHeight: 1.2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{title}</span>
                {subtitle && (
                    <span style={{
                        fontSize: SHEET_TOKENS.typography.hint.size,
                        color: colors.textMuted,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{subtitle}</span>
                )}
            </div>
            {onClose && (
                <button type="button" onClick={onClose} aria-label="Zavřít" style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: colors.textMuted, padding: 4, lineHeight: 0, flexShrink: 0,
                }}>
                    <X size={20} />
                </button>
            )}
        </div>
    );
}

// ───────────────────────────────────────────────────────────────────────
// Section — vizuálně oddělená sekce v sheetu, s uppercase nadpisem
// ───────────────────────────────────────────────────────────────────────
export function SheetSection({ title, hint, isDark, children, style = {} }) {
    const colors = getTextColors(isDark);
    return (
        <section style={{ display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.rowGap, ...style }}>
            {title && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{
                        fontSize: SHEET_TOKENS.typography.section.size,
                        fontWeight: SHEET_TOKENS.typography.section.weight,
                        letterSpacing: SHEET_TOKENS.typography.section.letterSpacing,
                        textTransform: SHEET_TOKENS.typography.section.textTransform,
                        color: colors.labelMuted,
                    }}>{title}</span>
                    {hint && (
                        <span style={{ fontSize: SHEET_TOKENS.typography.hint.size, color: colors.textMuted }}>
                            {hint}
                        </span>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

// ───────────────────────────────────────────────────────────────────────
// Row — horizontální / responsivní řádek pro label + control
// ───────────────────────────────────────────────────────────────────────
export function SheetRow({ children, gap = SHEET_TOKENS.spacing.rowGap, align = 'stretch', style = {} }) {
    return (
        <div style={{ display: 'flex', gap, alignItems: align, minWidth: 0, ...style }}>
            {children}
        </div>
    );
}

// ───────────────────────────────────────────────────────────────────────
// Alert — chybový / informativní / success bar
// ───────────────────────────────────────────────────────────────────────
export function SheetAlert({ kind = 'error', children, isDark, style = {} }) {
    const colors = getTextColors(isDark);
    const palettes = {
        error:   { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.30)',  color: colors.danger },
        warn:    { bg: 'rgba(255,138,0,0.12)',  border: 'rgba(255,138,0,0.30)',  color: '#FF8A00' },
        success: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.30)', color: colors.success },
        info:    { bg: 'rgba(12,192,223,0.12)', border: 'rgba(12,192,223,0.30)', color: '#0CC0DF' },
    };
    const palette = palettes[kind] || palettes.info;
    return (
        <div style={{
            padding: '10px 14px',
            background: palette.bg, border: `1px solid ${palette.border}`,
            borderRadius: SHEET_TOKENS.radius.card,
            color: palette.color, fontSize: SHEET_TOKENS.typography.hint.size, fontWeight: 600,
            lineHeight: 1.4,
            ...style,
        }}>
            {children}
        </div>
    );
}
